import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTable } from '../../api/api';
import { formatDate, truncateText } from '../../utils/helpers';

const PublicationCard = ({ pub }) => (
  <Link to={`/publication/${pub.slug}`} className="card">
    <div className="card-media">
      <img src={pub.image_url} alt={pub.title} loading="lazy" />
    </div>
    <div className="card-body">
      <div className="card-tags">
        <span className="tag">{pub.doc_type}</span>
        <span className={`tag access-${pub.access_level}`}>{pub.access_level}</span>
      </div>
      <h3>{pub.title}</h3>
      <p className="excerpt">{truncateText(pub.summary || '', 110)}</p>
      <div className="card-meta">
        <span>{pub.author || ''}</span>
        <span>{formatDate(pub.publish_date)}</span>
      </div>
    </div>
  </Link>
);

const IntelligenceEconomique = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getTable('tables/publications', { limit: 100 });
        const all = res.data || [];
        const filtered = all.filter((p) => p.category === 'Intelligence Économique').slice(0, 3);
        setPublications(filtered);
      } catch (e) {
        console.error('Erreur chargement publications:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <>
      <section className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Accueil</Link> / Intelligence Économique
          </div>
          <h1>Intelligence Économique</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '640px' }}>
            Anticiper, analyser et protéger l'information stratégique au service de la
            compétitivité des économies africaines.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container mission-grid">
          <div>
            <span className="section-kicker">Notre approche</span>
            <h2 className="section-title">Une méthodologie rigoureuse au service de la décision</h2>
            <p style={{ color: '#667180', marginBottom: '16px' }}>
              L'intelligence économique regroupe l'ensemble des actions coordonnées de recherche,
              de traitement et de diffusion de l'information utile aux acteurs économiques.
              À l'AEII, elle constitue le socle méthodologique de l'ensemble de nos travaux de recherche.
            </p>
            <p style={{ color: '#667180' }}>
              Nos analystes combinent veille documentaire, entretiens d'experts, traitement de
              données économiques et cartographie des acteurs pour produire des analyses actionnables
              destinées aux décideurs publics et privés.
            </p>
          </div>
          <img src="/images/cat-intelligence.jpg" alt="Analyse de données économiques" loading="lazy" />
        </div>
      </section>

      <section className="section bg-alt">
        <div className="container">
          <span className="section-kicker">Nos trois piliers</span>
          <h2 className="section-title">Les composantes de l'intelligence économique</h2>
          <div className="grid grid-3" style={{ marginTop: '32px' }}>
            <div className="value-card">
              <i className="fa-solid fa-binoculars"></i>
              <h4>Veille stratégique</h4>
              <p>Surveillance continue de l'environnement économique, réglementaire et concurrentiel des marchés africains.</p>
            </div>
            <div className="value-card">
              <i className="fa-solid fa-shield-halved"></i>
              <h4>Protection de l'information</h4>
              <p>Sécurisation des données stratégiques et sensibilisation à la cybersécurité des organisations.</p>
            </div>
            <div className="value-card">
              <i className="fa-solid fa-bullhorn"></i>
              <h4>Influence &amp; diffusion</h4>
              <p>Valorisation et diffusion ciblée des analyses auprès des décideurs et parties prenantes.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="section-kicker">À lire</span>
              <h2 className="section-title">Publications en Intelligence Économique</h2>
            </div>
            <Link to="/publications?category=Intelligence%20%C3%89conomique" className="btn btn-ghost">
              Voir toutes ces publications <i className="fa-solid fa-arrow-right"></i>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card" style={{ opacity: 0.6 }}>
                  <div className="card-media" style={{ background: '#e1e5eb' }} />
                  <div className="card-body">
                    <h3>Chargement...</h3>
                  </div>
                </div>
              ))}
            </div>
          ) : publications.length === 0 ? (
            <p>Aucune publication disponible pour le moment.</p>
          ) : (
            <div className="grid grid-3">
              {publications.map((pub) => (
                <PublicationCard key={pub.id} pub={pub} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default IntelligenceEconomique;